/**
 * Problem prompt extractor for OLX response tags (`<p>`, `<label>`, `<hint>`, etc.).
 */
import React, { useMemo } from "react";
const EXCLUDE_TAGS = new Set([
  "choicegroup",
  "checkboxgroup",
  "optioninput",
  "responseparam",
  "formulaequationinput",
  "textline",
  "numericalinput",
  "stringinput",
  "additional_answer",
]);

const PROMPT_TAGS = new Set(["p", "label", "description", "hint"]);

/**
 * Decodes HTML entities with a textarea.
 *
 * @param {string} input Encoded string.
 * @returns {string}
 */
function decodeHtmlEntities(input = "") {
  // Browser-native decode
  const txt = document.createElement("textarea");
  txt.innerHTML = input;
  return txt.value;
}

/**
 * Removes a leading `>>` prompt marker.
 *
 * @param {string} s Source text.
 * @returns {string}
 */
function stripLeadingChevrons(s = "") {
  // remove leading ">>" with optional spaces
  return s.replace(/^\s*>>\s*/g, "").trim();
}

/**
 * Converts literal `\\n` sequences into real newlines.
 *
 * @param {string} s Source text.
 * @returns {string}
 */
function normalizeText(s = "") {
  // handle literal "\n" sequences and real newlines
  return s.replace(/\\n/g, "\n").trim();
}

/**
 * Escapes HTML and converts newlines to `<br/>`.
 *
 * @param {string} text Source text.
 * @returns {string}
 */
function toSafeHtml(text = "") {
  // Escape HTML so prompt text can't inject markup
  const esc = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  // Render newlines as <br/>
  return esc.replace(/\n/g, "<br/>");
}

/**
 * Pulls main prompt and note text from children of a response tag.
 *
 * @param {Element} responseTag Response OLX element.
 * @returns {{ main: string[], notes: string[] }}
 */
function extractPromptParts(responseTag) {
  const main = [];
  const notes = [];

  if (!responseTag) return { main, notes };

  // Consider direct children of the response tag (numericalresponse/choiceresponse/etc)
  const children = Array.from(responseTag.children || []);

  for (const el of children) {
    const tagName = (el.tagName || "").toLowerCase();
    if (!tagName) continue;
    if (EXCLUDE_TAGS.has(tagName)) continue;
    if (!PROMPT_TAGS.has(tagName) && el.children?.length) {
      // If some wrapper tag exists, still try textContent
      // (but keep it conservative)
    }

    // Use textContent for decoded entities & clean output
    let text = decodeHtmlEntities(el.textContent || "");
    text = normalizeText(text);

    if (!text) continue;

    // Handle the custom "||" delimiter
    // left side = main question; right side(s) = note/hint
    const parts = text.split("||").map((p) => stripLeadingChevrons(p));
    const first = parts.shift();
    if (first) main.push(first);

    for (const rest of parts) {
      if (rest) notes.push(rest);
    }

    // If this tag is explicitly a description/hint, treat whole content as note
    if (tagName === "description" || tagName === "hint") {
      // If it had no ||, it ended up in main; move it to notes instead
      if (parts.length === 0 && first) {
        main.pop();
        notes.push(first);
      }
    }
  }

  return { main, notes };
}

/**
 * Renders prompt copy nested inside a problem response tag.
 *
 * @param {{ tag: Element, className?: string }} props
 */
export default function RenderResponsePrompt({ tag, className = "" }) {
  const { main, notes } = useMemo(() => extractPromptParts(tag), [tag]);

  if (!main.length && !notes.length) return null;

  return (
    <div className={`mb-3 ${className}`}>
      {main.map((line, idx) => (
        <div
          key={`main-${idx}`}
          className="mb-2 whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: toSafeHtml(line) }}
        />
      ))}

      {notes.map((line, idx) => (
        <div
          key={`note-${idx}`}
          className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded px-3 py-2 mb-2 whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: toSafeHtml(line) }}
        />
      ))}
    </div>
  );
}
